import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
} from '@angular/core';
import { AppStateFacade } from '@app-state';
import { TranslateService } from '@ngx-translate/core';

import {
    BehaviorSubject,
    Subject,
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export const UNKNOWN_OLDER_VERSION = 'undeclared';

interface IRobotVersion {
    count: number;
    version: string;
}

@Component({
    selector: 'ui-machine-robot-versions-display',
    templateUrl: './machine-robot-versions-display.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiMachineRobotVersionsDisplayComponent implements OnDestroy {
    @Input()
    public set robotVersions(robotVersions: IRobotVersion[]) {
        this._robotVersions = robotVersions;

        if (robotVersions.length === 1) {
            const [robotVersion] = robotVersions;
            this._isSingleVersion = robotVersion.count == null ||
                robotVersion.count === 1;
        }

        this.knownVersions = this._getKnownVersions(this._robotVersions);
        this.hasUnknownVersions = this._hasUnknownVersions(this._robotVersions);

        this._update();
    }

    public get robotVersions() {
        return this._robotVersions;
    }

    public knownVersions!: number;
    public hasUnknownVersions!: boolean;
    public versionsSummary$ = new BehaviorSubject<string>('');
    public value$ = new BehaviorSubject<string>('');

    private _isSingleVersion?: boolean;
    private _robotVersions!: IRobotVersion[];
    private _destroyed$ = new Subject();

    constructor(
        private _translateService: TranslateService,
        appState: AppStateFacade,
    ) {
        appState.language.change$
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(this._update);
    }

    ngOnDestroy() {
        this.value$.complete();
        this.versionsSummary$.complete();

        this._destroyed$.next();
        this._destroyed$.complete();
    }

    private _update = () => {
        const value = this._computeValue();
        this.value$.next(value);
        this.versionsSummary$.next(this._isSingleVersion ? value : this._getVersionsSummary());
    }

    private _getVersionsSummary() {
        const oldVersionText = this._translateService.instant('robot_versions_grid_version_undeclared');
        const unknownVersionText = this._translateService.instant('robot_versions_grid_version_unknown');

        const mappedVersions = this.robotVersions
            .map(robotVersion => {
                const version = robotVersion.version === UNKNOWN_OLDER_VERSION ?
                    oldVersionText :
                    robotVersion.version || unknownVersionText;
                const robotsCount = robotVersion.count;

                return this._translateService.instant('robot_versions_grid_version_tooltip_row_summary', {
                    version,
                    robotsCount,
                });
            })
            .sort();

        if (mappedVersions.length > 0) {
            // add Total row
            if (mappedVersions.length > 1) {
                const robotsCount = this.robotVersions.reduce((acc, curr) => acc += curr.count, 0);
                mappedVersions.push(
                    this._translateService.instant('robot_versions_grid_version_tooltip_total_summary', {
                        robotsCount,
                    }),
                );
            }
            return mappedVersions.join('\n');
        }

        return this._translateService.instant('robot_versions_grid_version_no_robots');
    }

    private _getKnownVersions(robotVersions: IRobotVersion[]) {
        return robotVersions
            .reduce((acc, curr) => acc + (curr.version ? 1 : 0), 0);
    }

    private _hasUnknownVersions(robotVersions: IRobotVersion[]) {
        return robotVersions
            .filter(item => !item.version)
            .length > 0;
    }

    private _computeValue() {
        if (this.robotVersions.length <= 1) {
            const [robotVersion] = this.robotVersions;

            if (robotVersion) {
                if (robotVersion.version === UNKNOWN_OLDER_VERSION) {
                    return this._translateService.instant('robot_versions_grid_version_undeclared');
                }
                return robotVersion.version ||
                    this._translateService.instant('robot_versions_grid_version_unknown');
            }
            return this._translateService.instant('robot_versions_grid_version_no_robots');
        }

        return this._translateService.instant('robot_versions_grid_version_count', {
            knownVersions: this.knownVersions + (this.hasUnknownVersions ? '+' : ''),
        });
    }
}
